import React, { useEffect, useMemo, useState } from "react";
import { Tag, Popover, Segmented } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { manpowerApis } from "../../../api/ManpowerNorms";
import ProjectScopeSelect from "../shared/ProjectScopeSelect";
import {
  MANPOWER_TEMPLATE_HEADERS,
  MANPOWER_TEMPLATE_COL_WIDTHS,
} from "./manpowerTemplate";

// Thin wrapper over the shared ExcelUploadStageModal (upload → validating →
// preview → committing → done | failed). Only the manpower-specific pieces live
// here: template generation, preview columns, row buckets, copy strings.
//
// Manpower is a FLAT master — one manpower type per row, no material lines and
// no forward-fill. Existing Item Codes are UPSERTED (rows are editable).

const generateManpowerTemplate = () => {
  const headers = [...MANPOWER_TEMPLATE_HEADERS];
  const sampleRows = [
    ["MP-MASON", "Mason - Skilled", "Day", 1, 950],
    ["MP-HELPER", "Helper - Unskilled", "Day", 1, 620],
    ["MP-CARP", "Carpenter - Skilled", "Day", 1, 900],
    ["MP-ELEC", "Electrician - Skilled", "Nos", 1, 1050],
    ["MP-OPER", "Equipment Operator", "Hour", 1, 180],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...MANPOWER_TEMPLATE_COL_WIDTHS];

  const instructions = [
    ["Manpower Norms Upload — Instructions"],
    [""],
    ["Item Code", "Required. Unique code for the manpower type (e.g. MP-MASON). One row per Item Code."],
    ["Item Description", "Optional. A readable name for the manpower type (e.g. Mason - Skilled)."],
    ["Units", "UOM name or short code (Day, Hour, Nos, MT...). Matched to the UOM Master; unrecognized units import with a warning."],
    ["PUC Factor", "Purchase unit conversion factor. Optional, defaults to 1. Max 4 decimals."],
    ["Unit Price", "In ₹, max 4 decimals. Optional — the rate for this manpower type."],
    [""],
    ["Note", "Existing Item Codes are UPSERTED: the description, units, PUC factor, unit price and status are updated in place (manpower rows are editable, unlike material norm lines). New Item Codes are created."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 20 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Manpower Norms");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "manpower-norms-upload-template.xlsx");
};

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return <Tag color="green">Valid</Tag>;
    },
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 110,
    render: (value: any) => {
      if (!value) return null;
      const color =
        value === "New"
          ? "green"
          : value === "Update"
          ? "gold"
          : "default"; // No Change
      return <Tag color={color}>{value}</Tag>;
    },
  },
  { title: "Excel Row", dataIndex: "excelRow", key: "excelRow", width: 90 },
  { title: "Item Code", dataIndex: "itemCode", key: "itemCode", width: 130 },
  { title: "Item Description", dataIndex: "itemDescription", key: "itemDescription", width: 220, ellipsis: true },
  { title: "Units", dataIndex: "units", key: "units", width: 100 },
  { title: "Units Match", dataIndex: "unitStatus", key: "unitStatus", width: 120 },
  { title: "PUC Factor", dataIndex: "pucFactor", key: "pucFactor", width: 100 },
  { title: "Unit Price (₹)", dataIndex: "unitPrice", key: "unitPrice", width: 120 },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

// Rows targeting an EXISTING item code carry __action 'Update' | 'No Change'
// (set by the backend helper); 'New' is a plain insert. The Updates tab appears
// only when any upsert rows exist.
const EXISTING_ITEM_ACTIONS = ["Update", "No Change"];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const updateRows = rows.filter(
    (row) => !row?.Error && EXISTING_ITEM_ACTIONS.includes(row?.__action)
  );
  const validRows = rows.filter(
    (row) => !row?.Error && !EXISTING_ITEM_ACTIONS.includes(row?.__action)
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `New (${validRows.length})`, rows: validRows },
    ...(updateRows.length
      ? [{ key: "updates", label: `Updates (${updateRows.length})`, rows: updateRows }]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const countItemCodes = (rows: any[]): number =>
  new Set(
    rows.filter((row) => !row?.Error && row?.itemCode).map((row) => row.itemCode)
  ).size;

const manpowerUploadConfig: ExcelUploadTypeConfig = {
  title: "Bulk Manpower Norms Upload",
  entityLabel: "Manpower Norms",
  templateSource: { kind: "client", generate: generateManpowerTemplate },
  startUpload: (formData) => manpowerApis.uploadExcel(formData),
  // Overridden per-instance below to carry the active projectId + category.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  bucketRows,
  summaryTags: (report: UploadReportState) => (
    <Tag color="blue">{countItemCodes(report.rows)} manpower type(s)</Tag>
  ),
  rowErrorLabel: (row) =>
    row.excelRow ? `Row ${row.excelRow}` : row.itemCode || "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Manpower Norms. Fill the manpower Excel file (one
        manpower type per row) accurately and completely, then upload it below.
        You'll see a full preview before anything is saved.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Checking rows and matching units to the UOM Master. This normally takes a few seconds.",
    committingHeading: "Saving manpower norms…",
    committingSub:
      "Inserting new manpower types and updating existing ones. This normally takes a few seconds.",
    commitCta: "Save Manpower Norms",
    doneHeading: "Manpower norms saved successfully",
    doneSub: (report: UploadReportState) => (
      <>
        {countItemCodes(report.rows) || ""} manpower type(s), {report.successCount}{" "}
        row(s) written. They are now visible in the Manpower list.
      </>
    ),
    creationFailedNote:
      "Save failed — no rows were written (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const ManpowerNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId = 0,
  category = "PRW",
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Scope the uploaded rows to this projectId (0 = org master) + category. */
  projectId?: number;
  category?: "PRW" | "OTHERS";
}) => {
  // The upload targets a project + category, defaulted from the list's current
  // selection but confirmable/changeable here so the upload is self-contained.
  const [scopeProjectId, setScopeProjectId] = useState<number>(projectId);
  const [scopeCategory, setScopeCategory] = useState<"PRW" | "OTHERS">(category);

  // Re-sync to the page's selection whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setScopeProjectId(projectId);
      setScopeCategory(category);
    }
  }, [open, projectId, category]);

  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...manpowerUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: scopeProjectId,
        category: scopeCategory,
      }),
      introSection: (
        <div className="mt-3">
          <label className="form-label mb-1">Upload into</label>
          <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
            <ProjectScopeSelect value={scopeProjectId} onChange={setScopeProjectId} />
            <Segmented
              value={scopeCategory}
              onChange={(val) => setScopeCategory(val as "PRW" | "OTHERS")}
              options={[
                { label: "PRW", value: "PRW" },
                { label: "Others", value: "OTHERS" },
              ]}
            />
          </div>
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            Uploaded rows are saved under this project and format.
          </div>
        </div>
      ),
    }),
    [scopeProjectId, scopeCategory]
  );
  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={scopeProjectId}
    />
  );
};

export default ManpowerNormUploadModal;
